import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, RouterProvider, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";

import "./index.css";
import { initTheme } from "./lib/theme";
import { ApiError, api, type Me } from "./lib/api";
import { Login } from "./pages/Login";
import { Layout } from "./pages/Layout";
import { Agents } from "./pages/Agents";
import { AgentPage, type AgentTab } from "./pages/Agent";
import { Connections } from "./pages/Connections";

// Portal Self-Service (agentes.revendai.com). Roteamento por código (sem
// routeTree.gen): 4 telas não justificam o gerador manual do Valquiria.

initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 3,
    },
  },
});

const rootRoute = createRootRoute({
  component: () => <QueryClientProvider client={queryClient}><Outlet /></QueryClientProvider>,
});

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: Login });

export const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: async () => {
    try {
      return { me: await api.get<Me>("/me") };
    } catch (e) {
      // Só 401/403 é logout; timeout/504 propaga (senão soluço de infra vira logout fantasma).
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) throw redirect({ to: "/login" });
      throw e;
    }
  },
  component: Layout,
});

const agentsRoute = createRoute({ getParentRoute: () => authedRoute, path: "/", component: Agents });
export const agentRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/agentes/$agentId",
  validateSearch: (s: Record<string, unknown>): { tab?: AgentTab } => ({
    tab: ["metricas", "ferramentas", "testar", "conexoes"].includes(String(s.tab)) ? (s.tab as AgentTab) : undefined,
  }),
  component: AgentPage,
});
const connectionsRoute = createRoute({ getParentRoute: () => authedRoute, path: "/conexoes", component: Connections });

const routeTree = rootRoute.addChildren([loginRoute, authedRoute.addChildren([agentsRoute, agentRoute, connectionsRoute])]);

const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error }) => (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, background: "var(--surface-app)" }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Não foi possível carregar</span>
      <span style={{ fontSize: 13, color: "var(--ink-muted)", maxWidth: 420, textAlign: "center" }}>
        {error instanceof Error && error.name === "TimeoutError" ? "O servidor demorou para responder. Tente de novo em instantes." : String(error)}
      </span>
      <button onClick={() => window.location.reload()} style={{ fontSize: 13, padding: "8px 18px", borderRadius: 8, cursor: "pointer", border: "1px solid var(--line-strong)", background: "var(--surface-hover)", color: "var(--ink)" }}>
        Tentar novamente
      </button>
    </div>
  ),
});

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
